import { Box, Flex, Text } from '@chakra-ui/react';
import {
  ArticleNavigationContext,
  NavigationVariant,
} from '@bridge/navigationStore';
import { useClientConfiguration } from '@texas/hooks/useClientConfiguration';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleVariantPicker } from './ArticleVariantPicker';
import { ContextNavigationItem } from './ContextNavigationItem';
import { getArticleItems, isStateActive } from './navigationItems';
import { useArticleNavigationActivity } from './useArticleNavigationActivity';

interface Props {
  activeState: string;
  context: ArticleNavigationContext;
  hasProductDevelopmentVariant: boolean;
}

function getDefaultVariant(
  variants: NavigationVariant[],
): NavigationVariant | null {
  const legacyVariants = variants
    .filter((variant) => !variant.isProductDevelopment)
    .sort((a, b) => a.name.localeCompare(b.name));

  const mainVariant = legacyVariants.find(
    (variant) => variant.isMain && !variant.archived,
  );
  if (mainVariant) return mainVariant;

  const activeVariant = legacyVariants.find((variant) => !variant.archived);
  if (activeVariant) return activeVariant;

  if (legacyVariants.length === 0) return null;
  return legacyVariants[0];
}

export function LegacyVariantNavigation({
  activeState,
  context,
  hasProductDevelopmentVariant,
}: Props) {
  const { t } = useTranslation();
  const { isProduction } = useClientConfiguration();
  const currentLegacyVariant = context.variants.find(
    (variant) =>
      variant.id === context.currentVariantId && !variant.isProductDevelopment,
  );
  const defaultVariant = useMemo(
    () => getDefaultVariant(context.variants),
    [context.variants],
  );
  const [rememberedVariantId, setRememberedVariantId] = useState<number | null>(
    () => currentLegacyVariant?.id ?? defaultVariant?.id ?? null,
  );

  useEffect(() => {
    if (currentLegacyVariant) {
      setRememberedVariantId(currentLegacyVariant.id);
      return;
    }

    if (
      rememberedVariantId !== null &&
      context.variants.some(
        (variant) =>
          variant.id === rememberedVariantId && !variant.isProductDevelopment,
      )
    )
      return;

    setRememberedVariantId(defaultVariant?.id ?? null);
  }, [
    context.variants,
    currentLegacyVariant,
    defaultVariant?.id,
    rememberedVariantId,
  ]);

  const rememberedVariant = context.variants.find(
    (variant) =>
      variant.id === rememberedVariantId && !variant.isProductDevelopment,
  );
  const selectedVariant =
    currentLegacyVariant ?? rememberedVariant ?? defaultVariant;
  const selectedVariantId = selectedVariant?.id ?? null;
  const navigation = useArticleNavigationActivity(selectedVariantId, {
    articleId: context.article.id,
    showGarp: true,
    syncedVariantId: context.article.syncedVariantId ?? null,
  });

  if (!selectedVariant) return null;

  return (
    <Box
      borderTopWidth={hasProductDevelopmentVariant ? '1px' : 0}
      borderColor="navigation.contextBorder"
      mt={3}
      pt={3}
    >
      <Text
        color="texas.burntSienna.500"
        fontFamily="instrumentSans"
        fontSize="11px"
        fontWeight="semibold"
        letterSpacing="0.08em"
        px={2.5}
        textTransform="uppercase"
      >
        {t('navigation.expiringView')}
      </Text>
      <ArticleVariantPicker
        context={context}
        selectedVariantId={selectedVariant.id}
      />
      <Flex direction="column" pt={3}>
        {getArticleItems(context, selectedVariant.id, isProduction).map(
          (item) => {
            const itemActivity = navigation.activity[item.route];
            return (
              <ContextNavigationItem
                key={item.route}
                label={t(item.label as 'navigation.overview')}
                route={item.route}
                navobj={{
                  articleId: context.article.id,
                  variantId: selectedVariant.id,
                }}
                active={
                  context.currentVariantId === selectedVariant.id &&
                  isStateActive(activeState, item.route)
                }
                meta={itemActivity?.label}
                metaTone={itemActivity?.tone}
              />
            );
          },
        )}
      </Flex>
    </Box>
  );
}
